import { useState } from 'react';
import { Skeleton } from '@solid/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  Plus,
  MessageCircle,
  CheckSquare,
  AlertCircle,
} from 'lucide-react';
import { projectsApi, discussionsApi, tasksApi, CalendarEvent } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { CreateDiscussionModal } from '@/components/modals/CreateDiscussionModal';
import { DriveConfigModal } from '@/components/modals/DriveConfigModal';
import { TaskDetailModal } from '@/components/modals/TaskDetailModal';
import { EventModal } from '@/components/modals/EventModal';
import { DiscussionCard, Discussion } from '@/components/discussions/DiscussionCard';
import { DiscussionStatus } from '@/components/ui/StatusDropdown';
import { ProjectCalendar } from '@/components/calendar/ProjectCalendar';
import { TaskItem } from '@/components/tasks/TaskItem';
import { HoursRemainingCard } from '@/components/project/HoursRemainingCard';
import { AtAGlanceCard } from '@/components/project/AtAGlanceCard';
import { FormsToFillBanner } from '@/components/project/FormsToFillBanner';
import { ProjectActivityCard } from '@/components/project/ProjectActivityCard';
import { projectOrg } from '@/components/project/ProjectIcon';
import { discussionLabels } from '@/lib/discussion-labels';
import { ImportantLinks } from '@/components/ImportantLinks';
import { useAuthStore } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { cn } from '@/lib/utils';
import { CompletionCelebration } from '@/components/ui/CompletionCelebration';

// Maximum discussions to show on the project detail page
const MAX_DISCUSSIONS_SHOWN = 5;
const MAX_TASKS_SHOWN = 8;
// Only discussions touched within this many business days make the overview
const RECENT_BUSINESS_DAYS = 10;

/** Midnight at the start of the day `n` business days (Mon–Fri) before today. Holidays aren't skipped. */
function startOfBusinessDaysAgo(n: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  let counted = 0;
  while (counted < n) {
    d.setDate(d.getDate() - 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) counted++;
  }
  return d;
}

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [showCreateDiscussion, setShowCreateDiscussion] = useState(false);
  const [showDriveConfig, setShowDriveConfig] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventInitialDate, setEventInitialDate] = useState<Date | undefined>(undefined);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);

  // Staff / admin of THIS project: Solid anywhere, a partner agency on its own
  const { isProjectStaff: isAgencyMember, isProjectAdmin: isAgencyAdmin } = useProjectRoles(projectId);

  // Check if user can edit a specific event
  // Agency admins can edit any event, others can only edit events they created
  const canEditEvent = (event: CalendarEvent) => {
    if (isAgencyAdmin) return true;
    return event.createdBy?.id === user?.id;
  };

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  // Fetch discussions with rich data (participants, attachments, lastReply)
  const { data: discussionsData } = useQuery({
    queryKey: ['discussions', projectId, 1, false],
    queryFn: () => discussionsApi.list(projectId!, 1, false),
    enabled: !!projectId,
  });

  // Fetch task lists with tasks for this project
  const { data: taskLists } = useQuery({
    queryKey: ['taskLists', projectId],
    queryFn: () => tasksApi.listTaskLists(projectId!),
    enabled: !!projectId,
  });

  // Status update mutation (handles all statuses including archived)
  const statusMutation = useMutation({
    mutationFn: ({ discussionId, status }: { discussionId: string; status: DiscussionStatus }) =>
      discussionsApi.updateStatus(discussionId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const handleStatusChange = (discussionId: string, status: DiscussionStatus) => {
    statusMutation.mutate({ discussionId, status });
  };

  // Track tasks that are fading out after completion
  const [completingTaskIds, setCompletingTaskIds] = useState<Set<string>>(new Set());

  // Task toggle mutation
  const toggleTaskMutation = useMutation({
    mutationFn: (taskId: string) => tasksApi.toggleComplete(taskId),
    onSuccess: (_, taskId) => {
      // Completing: play canvas animation (onComplete callback handles refresh)
      setCompletingTaskIds(prev => new Set(prev).add(taskId));
    },
  });

  // Update task status (for In Progress toggle)
  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      tasksApi.updateTask(taskId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Confirmation state for closing someone else's task
  const [confirmCloseTask, setConfirmCloseTask] = useState<{ id: string; title: string; assigneeName: string } | null>(null);

  // Delete task state and mutation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => tasksApi.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setDeleteConfirmId(null);
    },
    onError: (error) => {
      console.error('Failed to delete task:', error);
      setDeleteConfirmId(null);
    },
  });

  // Handle task toggle with permission check
  const handleTaskToggle = (task: any) => {
    const isAssignedToMe = task.assigneeId === user?.id;

    if (isAssignedToMe || !task.assigneeId) {
      // User is assignee or task is unassigned - toggle directly
      toggleTaskMutation.mutate(task.id);
    } else if (isAgencyAdmin) {
      // Admin closing someone else's task - show confirmation
      setConfirmCloseTask({
        id: task.id,
        title: task.title,
        assigneeName: task.assignee?.name || 'another user',
      });
    }
    // Non-admins can't close tasks assigned to others (button will be disabled)
  };

  const isArchived = !!project?.archivedAt;

  // Get discussions from API response - keep active, recently touched ones and sort by priority
  const allDiscussions: Discussion[] = discussionsData?.data || [];

  // Active (not closed/archived) with activity since the start of the day
  // RECENT_BUSINESS_DAYS business days ago. The list arrives newest-activity
  // first, so page 1 holds every recent one.
  // Important ones always show (unless archived), however old or closed:
  // an admin pinned them for everyone to see. The API sorts them first, so
  // page 1 holds them too.
  const recentCutoff = startOfBusinessDaysAgo(RECENT_BUSINESS_DAYS).getTime();
  const activeDiscussions = allDiscussions.filter(d =>
    d.discussionStatus !== 'archived' && (
      !!d.importantAt || (
        d.discussionStatus !== 'closed' &&
        !!d.lastActivityAt &&
        new Date(d.lastActivityAt).getTime() >= recentCutoff
      )
    )
  );

  // Sort discussions: priority status first based on user role
  const sortedDiscussions = [...activeDiscussions].sort((a, b) => {
    // Important first, newest mark first
    if (!!a.importantAt !== !!b.importantAt) return a.importantAt ? -1 : 1;
    if (a.importantAt && b.importantAt) return new Date(b.importantAt).getTime() - new Date(a.importantAt).getTime();

    // Priority status: "waiting_on_solid" for agency, "waiting_on_client" for clients
    const priorityStatus = isAgencyMember ? 'waiting_on_solid' : 'waiting_on_client';

    const aIsPriority = a.discussionStatus === priorityStatus;
    const bIsPriority = b.discussionStatus === priorityStatus;

    if (aIsPriority && !bIsPriority) return -1;
    if (!aIsPriority && bIsPriority) return 1;

    // Secondary sort by date (most recent first)
    return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
  });

  const displayedDiscussions = sortedDiscussions.slice(0, MAX_DISCUSSIONS_SHOWN);

  // Get all open tasks across all lists for the main area preview
  // Sort by priority (urgent first) then by due date (soonest first, nulls last)
  const PRIORITY_ORDER: Record<string, number> = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
  const allOpenTasks = (taskLists || [])
    .flatMap((list: any) =>
      (list.tasks || [])
        .filter((task: any) => task.status !== 'COMPLETED')
        .map((task: any) => ({ ...task, listName: list.name, listId: list.id }))
    )
    .sort((a: any, b: any) => {
      // Overdue tasks first, then by due date (soonest first), then by priority
      const now = Date.now();
      const aDue = a.dueDate ? new Date(a.dueDate).getTime() : null;
      const bDue = b.dueDate ? new Date(b.dueDate).getTime() : null;
      const aOverdue = aDue !== null && aDue < now;
      const bOverdue = bDue !== null && bDue < now;

      // Overdue tasks always come first
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;

      // Both overdue or both not overdue — sort by due date (soonest first, nulls last)
      if (aDue && bDue) {
        const diff = aDue - bDue;
        if (diff !== 0) return diff;
      }
      if (aDue && !bDue) return -1;
      if (!aDue && bDue) return 1;

      // Same due date bucket — sort by priority
      const pa = PRIORITY_ORDER[a.priority] ?? 4;
      const pb = PRIORITY_ORDER[b.priority] ?? 4;
      return pa - pb;
    })
    .slice(0, MAX_TASKS_SHOWN);

  const totalOpenTasks = (taskLists || [])
    .flatMap((list: any) => (list.tasks || []).filter((task: any) => task.status !== 'COMPLETED'))
    .length;

  if (isLoading) {
    return (
      <div aria-busy>
        <Skeleton className="mb-4 h-6 w-32" />
        <Skeleton className="mb-2 h-8 w-64" />
        <Skeleton className="mb-8 h-4 w-48" />
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <EmptyState
        icon={MessageSquare}
        title="Project not found"
        description="The project you're looking for doesn't exist."
        action={
          <Link to="/projects">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4" />
              Back to Projects
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="w-full">
      {/* Forms the viewer owes on this project: a call to action, above everything else */}
      <div className="mb-6 empty:hidden">
        <FormsToFillBanner projectId={projectId!} />
      </div>

      {/* Main Grid - Two columns on large screens */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Column - At a glance, Discussions, Tasks (Primary Content) */}
        <div className="xl:col-span-2 space-y-6">
          <AtAGlanceCard projectId={projectId!} />

          {/* Discussions Section */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-accent" />
                <h2 className="text-lg font-semibold text-secondary">{discussionLabels(project?.settings).plural}</h2>
                {sortedDiscussions.length > 0 && (
                  <span className="text-sm text-grey-400">({sortedDiscussions.length} active)</span>
                )}
              </div>
              {!isArchived && (
                <button
                  onClick={() => setShowCreateDiscussion(true)}
                  className="p-1.5 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-accent transition-colors"
                  title={`New ${discussionLabels(project?.settings).singular.toLowerCase()}`}
                >
                  <Plus className="w-5 h-5" />
                </button>
              )}
            </div>

            {displayedDiscussions.length === 0 ? (
              <div className="py-6 text-center">
                <MessageCircle className="w-10 h-10 text-grey-200 mx-auto mb-2" />
                <p className="text-grey-500 text-sm">
                  No {discussionLabels(project?.settings).singular.toLowerCase()} activity in the last {RECENT_BUSINESS_DAYS} business days
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {displayedDiscussions.map((discussion) => (
                  <DiscussionCard
                    key={discussion.id}
                    discussion={discussion}
                    projectId={projectId!}
                    isAgencyMember={isAgencyMember}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </div>
            )}

            {/* Footer: View All & Add Discussion */}
            <div className="mt-4 pt-4 border-t border-grey-100 space-y-2">
              <Link
                to={`/projects/${projectId}/discussions`}
                className="block text-sm text-accent hover:text-accent/80 transition-colors"
              >
                View all discussions →
              </Link>
              {!isArchived && (
                <button
                  onClick={() => setShowCreateDiscussion(true)}
                  className="flex items-center gap-1.5 text-sm text-grey-500 hover:text-accent transition-colors"
                >
                  <span className="text-grey-400">+</span>
                  Add a discussion
                </button>
              )}
              {project.archivedDiscussionCount > 0 && (
                <Link
                  to={`/projects/${projectId}/discussions?archived=true`}
                  className="block text-sm text-grey-400 hover:text-grey-600 transition-colors"
                >
                  {project.archivedDiscussionCount} archived
                </Link>
              )}
            </div>
          </Card>

          {/* Tasks Section - only when something is open */}
          {allOpenTasks.length > 0 && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-success" />
                  <h2 className="text-lg font-semibold text-secondary">Overdue and Important Tasks</h2>
                  <span className="text-sm text-grey-400">({totalOpenTasks} open)</span>
                </div>
                {!isArchived && (
                  <button
                    onClick={() => setShowCreateTaskModal(true)}
                    className="p-1.5 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-accent transition-colors"
                    title="Add task"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {allOpenTasks.map((task: any) => {
                  const isInProgress = task.status === 'IN_PROGRESS';
                  const isAssignee = task.assigneeId === user?.id;
                  // Permission: can edit if agency member or task creator
                  const canEdit = !isArchived && (isAgencyMember || task.createdById === user?.id);
                  // Permission: can toggle if agency member or assignee
                  const canToggle = !isArchived && (isAgencyMember || isAssignee);
                  const isCompleting = completingTaskIds.has(task.id);

                  return (
                    <div
                      key={task.id}
                      className="relative"
                      style={isCompleting ? { overflow: 'visible' } : undefined}
                    >
                      <div className={cn(isCompleting && 'animate-poof-row pointer-events-none')}>
                        <TaskItem
                          task={{
                            ...task,
                            taskListId: task.listId,
                            taskListName: task.listName,
                          }}
                          currentUserId={user?.id}
                          onClick={() => setSelectedTask({ ...task, taskListId: task.listId })}
                          onToggleComplete={canToggle ? () => handleTaskToggle(task) : undefined}
                          onToggleInProgress={!isArchived ? () => updateStatusMutation.mutate({
                            taskId: task.id,
                            status: isInProgress ? 'PENDING' : 'IN_PROGRESS',
                          }) : undefined}
                          onEdit={canEdit ? () => setSelectedTask({ ...task, taskListId: task.listId }) : undefined}
                          isToggling={toggleTaskMutation.isPending}
                          isUpdatingStatus={updateStatusMutation.isPending}
                          compact
                        />
                      </div>
                      <CompletionCelebration
                        show={isCompleting}
                        onComplete={() => {
                          setCompletingTaskIds(prev => {
                            const next = new Set(prev);
                            next.delete(task.id);
                            return next;
                          });
                          queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Footer: View All & Add Task */}
              <div className="mt-4 pt-4 border-t border-grey-100 space-y-3">
                {/* View All Tasks Link */}
                <Link
                  to={`/projects/${projectId}/tasks`}
                  className="inline-flex items-center gap-2 text-accent hover:underline font-medium text-sm"
                >
                  View all task lists →
                </Link>

                {/* Add a task button */}
                {!isArchived && (
                  <button
                    onClick={() => setShowCreateTaskModal(true)}
                    className="flex items-center gap-2 text-sm text-grey-500 hover:text-accent transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add a task
                  </button>
                )}
              </div>
            </Card>
          )}

        </div>

        {/* Right Column - Hours, Activity, Calendar, Important Links. Each hides itself
            when it has nothing to say. */}
        <div className="space-y-6">
          {/* Support hours, when this project draws a bank. Renders nothing at
              all otherwise — most projects are not support agreements. */}
          <HoursRemainingCard projectId={projectId!} clientOrgId={projectOrg(project)?.id} />

          {/* Your Activity for this project; hidden when there's none */}
          <ProjectActivityCard projectId={projectId!} />

          {/* Calendar Section - only when this week has something on it */}
          <ProjectCalendar
            projectId={projectId!}
            hideWhenEmpty
            onAddEvent={!isArchived ? (date) => {
              setEventInitialDate(date);
              setEditingEvent(null);
              setShowEventModal(true);
            } : undefined}
            onEventClick={(event) => {
              setEditingEvent(event);
              setEventInitialDate(undefined);
              setShowEventModal(true);
            }}
            onTaskClick={(task) => setSelectedTask({ ...task, taskListId: task.list.id })}
            canEditEvent={canEditEvent}
          />

          {/* Important Links Section */}
          <ImportantLinks
            projectId={projectId!}
            links={project.links || []}
            canEdit={!!isAgencyAdmin && !isArchived}
          />
        </div>
      </div>

      {/* Modals */}
      <CreateDiscussionModal
        isOpen={showCreateDiscussion}
        projectId={projectId!}
        onClose={() => setShowCreateDiscussion(false)}
        onSuccess={(discussion) => navigate(`/projects/${projectId}/discussions/${discussion.id}`)}
      />

      <DriveConfigModal
        isOpen={showDriveConfig}
        projectId={projectId!}
        onClose={() => setShowDriveConfig(false)}
      />

      {/* Task Detail Modal */}
      {selectedTask && project && (
        <TaskDetailModal
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          task={selectedTask}
          projectId={projectId!}
          listId={selectedTask.taskListId}
          organizationId={project.organizationId}
        />
      )}

      {/* Create Task Modal (from Add a task button) */}
      {project && (
        <TaskDetailModal
          create
          isOpen={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          projectId={projectId!}
          taskLists={taskLists?.map((list: any) => ({ id: list.id, name: list.name })) || []}
        />
      )}

      {/* Event Modal */}
      <EventModal
        isOpen={showEventModal}
        projectId={projectId!}
        onClose={() => {
          setShowEventModal(false);
          setEventInitialDate(undefined);
          setEditingEvent(null);
        }}
        event={editingEvent}
        initialDate={eventInitialDate}
      />

      {/* Confirmation dialog for closing someone else's task */}
      {confirmCloseTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-secondary/50 backdrop-blur-sm"
            onClick={() => setConfirmCloseTask(null)}
          />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-warning" />
              </div>
              <h3 className="text-lg font-semibold text-secondary">Complete Task?</h3>
            </div>
            <p className="text-sm text-grey-600 mb-2">
              This task is assigned to <strong>{confirmCloseTask.assigneeName}</strong>.
            </p>
            <p className="text-sm text-grey-500 mb-6">
              Are you sure you want to mark "{confirmCloseTask.title}" as complete?
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setConfirmCloseTask(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  toggleTaskMutation.mutate(confirmCloseTask.id);
                  setConfirmCloseTask(null);
                }}
              >
                Yes, Complete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete task confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">Delete Task?</h3>
            <p className="text-grey-500 text-sm mb-6">
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteTaskMutation.mutate(deleteConfirmId)}
                isLoading={deleteTaskMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
